import { HTMLAttributes, useEffect, useMemo } from 'react'
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import Image from 'next/image'

import Button from '@/components/base/button'
import Media from '@/components/base/media'
import Spacer from '@/components/sections/spacer'
import { isHappa, useEmblaCarouselHook } from '@/hooks'
import { useLocaleStore } from '@/store/locale'
import { truncateText } from '@/utils/strings'
import { LANGUAGES } from '@/constants'

import { AdvertisementSearchParams, useAdvertisementsSearch } from '@/api/advertisements'
import { RequestParams, WPLink, WPMedia } from '@/types'

export type ProposalsProps = HTMLAttributes<HTMLDivElement> & {
  title: string
  link: WPLink
  media: WPMedia
  request_params: RequestParams
  is_first?: boolean
}

const Proposals = ({ title, link, media, request_params, is_first, ...props }: ProposalsProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)

  const searchParams = useMemo<AdvertisementSearchParams | undefined>(() => {
    if (!currentLocale) return undefined

    // Convert ids string to array of numbers
    const idsArray = request_params.ids
      ? request_params.ids
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id))
      : undefined

    return {
      city_id: request_params.city_id ? parseInt(request_params.city_id) : undefined,
      order_by: request_params.order_by,
      per_page: request_params.per_page,
      locale: currentLocale,
      ids: idsArray && idsArray.length > 0 ? idsArray : undefined
    }
  }, [request_params, currentLocale])

  const { collection: items, isLoading: loading } = useAdvertisementsSearch(searchParams)

  const { emblaRef, scrollPrev, scrollNext, nextBtnDisabled, prevBtnDisabled, emblaApi } =
    useEmblaCarouselHook({
      loop: false,
      align: 'start'
    })

  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
    }
  }, [items?.length, emblaApi])

  const renderSkeleton = () => (
    <Box
      sx={(theme) => ({
        textDecoration: 'none',
        flex: '0 0 60%',
        minWidth: 0,
        background: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius4,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
      })}
    >
      <Skeleton variant="text" width="60%" height={30} />
      <Spacer size="xxs" />
      <Skeleton variant="rectangular" width="3.2rem" height="0.1rem" />
      <Spacer size="xxs" />
      <Box sx={{ display: 'flex', gap: 1 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width="9rem"
            height="9rem"
            sx={{ borderRadius: (theme) => theme.shape.borderRadius2 }}
          />
        ))}
      </Box>
      <Spacer size="xs" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="80%" height={21} />
      </Box>
      <Spacer size="xs" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width="4rem"
            height="1.8rem"
            sx={{ borderRadius: (theme) => theme.shape.borderRadius4 }}
          />
        ))}
      </Box>
    </Box>
  )

  return (
    <Box {...props}>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 6 }}>
            <Box
              sx={{
                width: '100%',
                aspectRatio: '672/762',
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                borderRadius: (theme) => theme.shape.borderRadius6
              }}
            >
              <Media {...media} />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 4, sm: 8, md: 6 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}
            >
              {title ? (
                <Typography
                  variant="h3"
                  component={is_first ? 'h1' : 'h2'}
                  fontWeight={isHappa ? '700' : '400'}
                  color={isHappa ? 'primary' : 'text.primary'}
                >
                  {title}
                </Typography>
              ) : null}
              <Spacer size="xs" />
              {link?.url ? (
                <Button href={link.url} variant="text" icon="search" color="warning">
                  {link.title}
                </Button>
              ) : null}
              <Spacer size="md" />
            </Box>
            {loading || (items && items.length > 0) ? (
              <Box sx={{ position: 'relative' }}>
                <Box ref={emblaRef} sx={{ overflow: 'hidden', p: '2rem', m: '-2rem' }}>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    {loading
                      ? Array.from({ length: request_params.per_page || 3 }).map((_, i) => (
                          <div key={`skeleton-${i}`}>{renderSkeleton()}</div>
                        ))
                      : items?.map((item, index) => (
                          <Link
                            key={index}
                            href={`${currentLocale !== LANGUAGES[0] ? `/${currentLocale}` : ''}/advertisements/${item.id}`}
                            sx={(theme) => ({
                              textDecoration: 'none',
                              flex: { xs: '0 0 100%', sm: '0 0 60%' },
                              minWidth: 0,
                              background: isHappa ? '#ffffff' : theme.palette.grey[100],
                              borderRadius: isHappa
                                ? theme.shape.borderRadius2
                                : theme.shape.borderRadius4,
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              boxShadow: isHappa ? theme.shadows[2] : 'none',
                              transition: `all 0.3s ${theme.customEasings.outCubic}`,
                              '&:hover': {
                                background: theme.palette.grey[200]
                              }
                            })}
                          >
                            {isHappa && item.property?.city?.name?.[currentLocale!] ? (
                              <Typography variant="body2" color="text.secondary">
                                {item.property.city.name[currentLocale!]}
                              </Typography>
                            ) : null}
                            <Spacer size="xxs" />
                            {item.title ? (
                              <Typography
                                variant="h6"
                                component="span"
                                color={isHappa ? '#212121' : 'primary'}
                                fontWeight={isHappa ? '700' : '400'}
                              >
                                {truncateText(item.title?.[currentLocale!], 50)}
                              </Typography>
                            ) : null}
                            <Spacer size="xxs" />
                            <Divider
                              sx={() => ({
                                width: '3.2rem',
                                height: '0.1rem'
                              })}
                            />
                            <Spacer size="xxs" />
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
                              {item.attachments?.slice(0, 3).map((img, i) => (
                                <Box
                                  key={i}
                                  sx={(theme) => ({
                                    width: '9rem',
                                    aspectRatio: '1/1',
                                    borderRadius: isHappa
                                      ? theme.shape.borderRadius1
                                      : theme.shape.borderRadius4,
                                    overflow: 'hidden'
                                  })}
                                >
                                  <Image
                                    src={`/images/${img.thumb_path || img.path}`}
                                    alt=""
                                    width={100}
                                    height={100}
                                    style={{ objectFit: 'cover' }}
                                  />
                                </Box>
                              ))}
                            </Box>
                            <Spacer size="xxs" />
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                justifyContent: 'flex-start'
                              }}
                            >
                              {item.price ? (
                                <Typography
                                  variant="textLarge"
                                  color="primary"
                                  sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'inherit' }}
                                >
                                  €{item.price}
                                </Typography>
                              ) : null}
                              {item.description ? (
                                <Typography
                                  variant={isHappa ? 'body1' : 'textLarge'}
                                  color="text.primary"
                                >
                                  {truncateText(item.description?.[currentLocale!], 100)}
                                </Typography>
                              ) : null}
                            </Box>
                            <Spacer size="xs" />
                            {[...(item.features || []), ...(item.property?.features || [])]
                              ?.length ? (
                              <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                                {[...(item.features || []), ...(item.property?.features || [])]
                                  .filter(
                                    (f) =>
                                      f.group?.slug === 'struct_type' ||
                                      f.group?.slug === 'accessible'
                                  )
                                  .sort((a, b) => {
                                    const order = ['struct_type', 'accessible']
                                    return (
                                      order.indexOf(a.group?.slug || '') -
                                      order.indexOf(b.group?.slug || '')
                                    )
                                  })
                                  .map((f) => ({ label: f.name?.[currentLocale!] || '' }))
                                  ?.map(({ label }, index) => (
                                    <Chip key={`chip-${index}`} label={label} variant="color" />
                                  ))}
                              </Stack>
                            ) : null}
                          </Link>
                        ))}
                  </Box>
                </Box>
                <Spacer size="md" />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="medium"
                    onClick={scrollPrev}
                    disabled={prevBtnDisabled}
                    color="secondary"
                  >
                    <SvgIcon fontSize="small">
                      <use href="#ico-arrow-prev" />
                    </SvgIcon>
                  </IconButton>
                  <IconButton
                    size="medium"
                    onClick={scrollNext}
                    disabled={nextBtnDisabled}
                    color="secondary"
                  >
                    <SvgIcon fontSize={isHappa ? 'small' : 'medium'}>
                      <use href="#ico-arrow-next" />
                    </SvgIcon>
                  </IconButton>
                </Box>
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Proposals
